import React, { useContext } from 'react';
import cx from 'utils/classnames';
import Rating from 'components/base/rating/Rating';
import useAppendQueryParams from 'hooks/custom/useAppendQueryParams';
import { QUERY_PARAMS } from 'consts';
import { GetSchoolListingResults } from 'types';
import ResultCardContext from './ResultCardContext';
import ResultCardLogo from './ResultCardLogo';
import ResultCardLink from './ResultCardLink';
import ResultCardTitle from './ResultCardTitle';
import ResultCardInfoRow from './ResultCardInfoRow';
import ResultCardFooter from './ResultCardFooter';
import SchoolDates from './SchoolDates';

const { IMPRESSION_GUID_PARAM } = QUERY_PARAMS;

interface GridCardProps {
  cardRef: React.RefObject<HTMLElement | undefined>;
  schoolInfo: GetSchoolListingResults['results'][number];
  isLoading?: boolean;
  isMobile?: boolean;
  logoImg?: { url?: string } | string;
  badge?: string;
  isProgramView: boolean;
  currentLink: string;
  onLogoClick: () => void;
  onTitleClick: () => void;
  onCTAclick: () => void;
  onProgramLinkClick: () => void;
  onTotalCountClick: () => void;
}

function getHighlightsHtml(
  highlights: GetSchoolListingResults['results'][number]['highlights']
): string {
  if (!highlights) {
    return '';
  }
  return Array.isArray(highlights) ? highlights.join('') : highlights;
}

export default function GridCard({
  cardRef,
  schoolInfo,
  isLoading,
  isMobile,
  logoImg,
  badge,
  isProgramView,
  currentLink,
  onLogoClick,
  onTitleClick,
  onCTAclick,
  onProgramLinkClick,
  onTotalCountClick,
}: GridCardProps): React.JSX.Element {
  const { ratingTooltipText } = useContext(ResultCardContext);
  const highlights = getHighlightsHtml(schoolInfo.highlights);
  const selectedProgramName = schoolInfo.selectedProgram?.label;
  const schoolId = schoolInfo?.id?.toString();
  const hasDetails = Boolean(highlights);

  const learnMoreLink = useAppendQueryParams(currentLink, {
    [IMPRESSION_GUID_PARAM]: schoolInfo.impressionGuid,
    click_location: isProgramView
      ? 'best matching card | learn-more'
      : 'school card | learn-more',
  });
  return (
    <article
      ref={cardRef as React.RefObject<HTMLElement>}
      data-testid="gridCard"
      className={cx('gridCard', {
        'gridCard--isLoading': isLoading,
        'gridCard--hasBadge': Boolean(badge),
      })}
    >
      {badge && (
        <div className="gridCard__header">
          <div className="gridCard__badge">{badge}</div>
        </div>
      )}
      <div
        className={cx('gridCard__main', {
          'gridCard__main--hasDetails': hasDetails,
        })}
      >
        <div className="gridCard__logo">
          {
            // @ts-expect-error JS component; inferred props don't match PropTypes
            <ResultCardLogo
              isLoading={isLoading}
              linkUrl={currentLink}
              logoImg={logoImg}
              onClick={onLogoClick}
              impressionGuid={schoolInfo.impressionGuid}
              isProgramView={isProgramView}
            />
          }
        </div>
        <div className="gridCard__titleBlock">
          <ResultCardTitle
            className="gridCard__title"
            impressionGuid={schoolInfo.impressionGuid}
            isProgramView={isProgramView}
            linkUrl={currentLink}
            onClick={onTitleClick}
          >
            {schoolInfo.name}
          </ResultCardTitle>
          <SchoolDates
            className="gridCard__startDate"
            schoolDates={schoolInfo.schoolDates}
          />
        </div>
        <div className="gridCard__rating">
          <ResultCardInfoRow
            className="hasRating"
            label="Editor's Rating"
            toolTip={{
              label: 'More Info',
              text: ratingTooltipText,
            }}
            value={
              isLoading ? (
                ''
              ) : (
                <Rating rating={schoolInfo.rating || 0} id={schoolId} />
              )
            }
          />
        </div>
        <div className="gridCard__actions">
          <ResultCardLink
            linkUrl={learnMoreLink}
            className="gridCard__cta"
            onClick={onCTAclick}
          >
            {isProgramView ? 'Visit Site' : 'Find Your Program'}
          </ResultCardLink>
        </div>
        {hasDetails && (
          <div className="gridCard__details">
            {highlights && (
              <ResultCardInfoRow
                label="School Highlights"
                value={highlights}
                className="hasHighlights"
              />
            )}
          </div>
        )}
      </div>
      <div className="gridCard__footer">
        <ResultCardFooter
          description={schoolInfo.selectedProgram?.shortDesc}
          review={schoolInfo.description}
          // @ts-expect-error JS component; inferred props don't match PropTypes
          highlights={schoolInfo.highlights}
          isLoading={isLoading}
          isMobile={isMobile}
          isProgramView={isProgramView}
          onProgramLinkClick={onProgramLinkClick}
          onTotalCountClick={onTotalCountClick}
          selectedProgramName={selectedProgramName}
          otherPrograms={schoolInfo.otherPrograms}
          programCount={schoolInfo.programCount}
          cardLink={currentLink}
          schoolId={schoolInfo.id}
          impressionGuid={schoolInfo.impressionGuid}
        />
      </div>
    </article>
  );
}
